import { Button } from "@radix-ui/themes";
import { useCallback, useMemo, useState } from "react";
import { Skill } from "@/v2/entities/skill/Skill.model";
import { toast } from "sonner";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import { skillTemplates, SkillTemplate, skillTemplateSlugs } from "./templates";
import { SkillsModalContent } from "./skills-modal-content";
import { Dialog } from "@/components/shared/dialog";
import { useSkillApi } from "@/v2/entities/skill/Skill.api";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { PlusIcon } from "@radix-ui/react-icons";

interface CreateSkillProps {
  existingSkills?: Skill[];
  onSuccess: () => Promise<unknown>;
  onBrowserSkill?: () => void;
}

export function CreateSkill({
  existingSkills = [],
  onSuccess,
  onBrowserSkill,
}: CreateSkillProps) {
  const navigate = useNavigate();
  const { createSkill } = useSkillApi();

  const snowflakeSkillsEnabled = useFlag("snowflake-skills", false);
  const hitlApprovalEnabled = useFlag("hitlApprovalRequests", false);
  const calculateTimeDifferenceEnabled = useFlag("calculateTimeDifference", false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [skillName, setSkillName] = useState("");
  const [description, setDescription] = useState("");
  const [curl, setCurl] = useState("");
  const [slug, setSlug] = useState("");
  const [selectedTab, setSelectedTab] = useState("custom");
  const [selectedTemplate, setSelectedTemplate] =
    useState<SkillTemplate | null>(null);
  const [templateSearch, setTemplateSearch] = useState("");

  const { mutate: createSkillMutation, isLoading: isCreateSkillLoading } =
    useMutation({
      mutationKey: ["createSkill"],
      mutationFn: createSkill,
      onSuccess: async () => {
        toast("skill created successfully");
        setDialogOpen(false);
        resetForm();
        await onSuccess();
      },
      onError: () => {
        toast.error("Failed to create skill");
      },
    });

  // Filter templates based on search and feature flags
  const filteredTemplates = useMemo(
    () =>
      (Object.values(skillTemplates) as SkillTemplate[]).filter((template) => {
        if (
          template.slug === "snowflake_sql_query" &&
          !snowflakeSkillsEnabled
        ) {
          return false;
        }
        if (
          template.featureFlag === "hitlApprovalRequests" &&
          !hitlApprovalEnabled
        ) {
          return false;
        }
        if (
          template.featureFlag === "calculateTimeDifference" &&
          !calculateTimeDifferenceEnabled
        ) {
          return false;
        }
        return (
          template.name.toLowerCase().includes(templateSearch.toLowerCase()) ||
          template.description
            .toLowerCase()
            .includes(templateSearch.toLowerCase()) ||
          template.category.toLowerCase().includes(templateSearch.toLowerCase())
        );
      }),
    [
      skillTemplates,
      templateSearch,
      snowflakeSkillsEnabled,
      hitlApprovalEnabled,
      calculateTimeDifferenceEnabled,
    ]
  );

  const resetForm = useCallback(() => {
    setSkillName("");
    setDescription("");
    setCurl("");
    setSlug("");
    setSelectedTemplate(null);
    setSelectedTab("custom");
    setTemplateSearch("");
  }, []);

  const handleDialogClose = useCallback(
    (open: boolean) => {
      setDialogOpen(open);
      if (!open) {
        resetForm();
      }
    },
    [resetForm]
  );

  const handleTemplateSelect = useCallback(
    (template: SkillTemplate) => {
      if (template.slug === "snowflake_sql_query") {
        const nameParam = encodeURIComponent(template.name);
        const slugParam = encodeURIComponent(template.slug);
        navigate(
          `/automation-center/skills/create/snowflake/${nameParam}/${slugParam}`
        );
        setDialogOpen(false);
        return;
      }
      setSelectedTemplate(template);
      setSkillName(template.name);
      setDescription(template.description);
      setCurl(template.curl);
      setSlug(template.slug);
      // Don't switch tabs - user can create directly from library
    },
    [navigate]
  );

  const handleCreateSkill = useCallback(async () => {
    createSkillMutation({
      name: skillName,
      description,
      curl,
      slug: slug.toLowerCase().replace(/ /g, "_"),
    });
  }, [skillName, description, curl, slug, createSkillMutation, resetForm]);

  const handleOpenDialog = useCallback(() => {
    setDialogOpen(true);
  }, [setDialogOpen]);

  const actions = useMemo(() => {
    const items = [
      {
        label: "Use Template",
        onClick: () => {
          setSelectedTab("templates");
          handleOpenDialog();
        },
      },
      {
        label: "Custom API",
        onClick: () => navigate("/automation-center/skills/create"),
      },
    ];

    if (onBrowserSkill) {
      items.push({
        label: "Browser Skill",
        onClick: onBrowserSkill,
      });
    }

    return items;
  }, [navigate, handleOpenDialog, onBrowserSkill]);

  return (
    <>
      <DropdownMenu items={actions}>
        <DropdownMenu.Trigger>
          <Button highContrast color="gray">
            <PlusIcon />
            Create Skill
          </Button>
        </DropdownMenu.Trigger>
      </DropdownMenu>

      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <Dialog.Content
          className="w-[800px] max-h-[90vh] flex flex-col overflow-hidden"
          style={{ height: "90vh" }}
          isPortal={true}
          size="large"
        >
          <SkillsModalContent
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            templateSearch={templateSearch}
            setTemplateSearch={setTemplateSearch}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            filteredTemplates={filteredTemplates}
            handleTemplateSelect={handleTemplateSelect}
            skillName={skillName}
            setSkillName={setSkillName}
            slug={slug}
            setSlug={setSlug}
            description={description}
            setDescription={setDescription}
            curl={curl}
            setCurl={setCurl}
            handleCreateSkill={handleCreateSkill}
            loading={isCreateSkillLoading}
            existingSkills={existingSkills}
            handleClose={() => setDialogOpen(false)}
            onlyTemplates={true}
          />
        </Dialog.Content>
      </Dialog>
    </>
  );
}
